import type { DefaultError, FetchInfiniteQueryOptions, QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * On initial page load, blocks until data is fetched (for SSR / first paint).
 * On subsequent calls (filter changes), fires a background prefetch so the
 * route loader resolves immediately and the pending skeleton never shows.
 */
export async function ensureOrPrefetchInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  queryClient: QueryClient,
  cacheKeyPrefix: QueryKey,
  queryOptions: FetchInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
) {
  const hasCachedData = queryClient
    .getQueryCache()
    .findAll({ queryKey: cacheKeyPrefix })
    .some((q) => q.state.data != null);

  if (hasCachedData) {
    void queryClient.prefetchInfiniteQuery(queryOptions);
  } else {
    await queryClient.ensureInfiniteQueryData(queryOptions);
  }
}
